import { inRecordOrder, type PaymentMean } from "@hakili/domain-types";
import { formatMoney, t } from "@hakili/i18n";
import { ArrowLeft, CheckCircle2, Shield } from "lucide-react";
import { useState } from "react";

/**
 * `round C3-A`, `C3-A.2`: **`WF-CLI-10` §1 to §5, the payment screen.**
 *
 * ## Every figure is a door's, and this file composes nothing
 *
 * Three answers arrive already whole and are rendered side by side:
 *
 * - `GET /me/dossiers/{id}/fees` , what is due, and on the financing path the
 *   two frais de dossier from `payments`' own versioned percentage;
 * - `POST /api/v1/simulation/quote` with `fileFeeAmount` , what including the
 *   Hakili fee in the loan costs over the term, beside the instalment, from one
 *   reading of one plan;
 * - `GET /me/dossiers/{id}/mandate` , the five clauses and their version.
 *
 * **The screen multiplies nothing and subtracts nothing.** `02` §F2 fixes the
 * fee's arithmetic with a golden test *because two screens computing one figure
 * differently is the failure mode the mockups explicitly guard against*, and
 * the surcharge is two amortisations differenced, which is worse.
 *
 * ## The mandate text is SERVED, and this file keeps no copy
 *
 * The clauses come down with their version and the acceptance echoes that
 * version back. A tie between a catalogue and a domain constant holds in CI and
 * not in a buyer's hand: a stale bundle could print version 1 while the door
 * stored the digest of version 2, and the stored evidence would be of a text he
 * never saw. The catalogue keeps the LABELS around the text and never the text.
 *
 * ## A catalogue KEY is data, and a rename pass nearly ate three
 *
 * The English-naming pass replaced identifiers by substring and reached inside
 * `t("...")`, turning `accepterPourContinuer` into `acceptedrPourContinuer`,
 * `chargeInconnu` into `ownerUnknown` and `lireMandat` into `readMandate`.
 * **`09-01`'s own first answer is that a wire value is DATA and never a name**,
 * and a catalogue key is exactly that: the map must match the catalogue, and
 * renaming it breaks the match.
 *
 * Neither `tsc` nor `eslint` could see it , `t` takes a `string` , and it threw
 * only when a browser rendered the screen: the corridor found it as « Une
 * erreur inattendue est survenue », the error boundary, which is what a founder
 * would have met.
 *
 * ## The shades are the PALETTE's, and four of the drawing's are not
 *
 * `palette-is-closed` refused four of the drawing's own classes, copied here
 * without checking , emerald at 100 and 600, grey at 600 and 700. **A shade the
 * closed palette does not carry is not a colour, it is silence**, the law
 * `round C2` booked, and it caught this file before a founder could meet a
 * refund card with no border and an icon in black.
 *
 * **They are described here and not written**, because this instrument reads
 * the FILE: a comment naming the offending class is itself an occurrence, and
 * the first cure failed on its own explanation.
 *
 * The architect ruled them at `C3-A.2`: the design system of 6 September wins
 * if it names a token for the role, otherwise the palette is amended with the
 * drawing's shade. **That document does not exist in this repository** , none
 * on disk and none in git's history , so the fallback applied and all four
 * entered the preset and `04` §4.6.3. The drawing's own shades are used here,
 * and the declared debt shrank by THIRTY-THREE lines across four apps and the
 * kit, with no screen edited: those greys had been silent everywhere.
 *
 * ## The chargé line is a DASH while no file has an owner
 *
 * `WF-CLI-10` §2 wants « X suit votre dossier. » `I17` says a file is assigned
 * the instant it is born, and `P1-DOS-010`, the item that implements it, is
 * unbuilt , so every client-created file is unassigned today and the line has
 * no name to print. It prints a dash rather than a guess (`R81`), and the line
 * fills itself the day `round C4` seats a chargé.
 */

export interface Fee {
  /** `PAY-P`: `null` while no price is published , the screen prints a dash and the door's sentence. */
  readonly clientAmount: number | null;
  readonly quoted: boolean;
  readonly unpublished?: string;
}

const DASH = t("common.valeur.tiret");

/** The amount as the screen prints it: the figure, or a dash (`R81`). */
const amountOf = (fee: Fee): string => (fee.clientAmount === null ? DASH : formatMoney(fee.clientAmount));

export interface FileFeesView {
  readonly hakiliAmount: number;
  readonly bankAmount: number;
  readonly baseAmount: number;
  readonly settingsVersion: number;
}

export interface ServedMandate {
  readonly version: number;
  readonly clauses: readonly { readonly title: string; readonly body: string }[];
  readonly accepted: { readonly feeFinancingRequested: boolean } | null;
  /** R-G (`L-3`): the estimate the door serves with the text , printed, then echoed at acceptance. */
  readonly estimate: { readonly instalmentAmount: number; readonly indicativeRatePct: string } | null;
}

/** One of `WF-CLI-10` §6's FIVE (`round PAY-M`), as the fees door answers it. */
export interface MeanView {
  readonly code: PaymentMean;
  readonly available: boolean;
  /** Why it is not offered , never omitted, never silent (`R81`). */
  readonly reason: string | null;
}

/** Hakili's own account, printed on the transfer step and nowhere else. */
/**
 * 16 October 2026 (H-2026-014): **one destination per method.** A transfer is paid into Hakili's
 * bank account, a mobile means into Hakili's number for THAT operator , the client is never shown
 * another method's details.
 */
export type PaymentDestination =
  ({ readonly kind: "bank" } & HakiliAccount) | { readonly kind: "mobile"; readonly number: string };

export interface HakiliAccount {
  readonly bankName: string;
  readonly accountName: string;
  readonly accountNumber: string;
}

/**
 * Each means' two sentences, **keyed by the WIRE's own code**.
 *
 * `09-01`'s first answer: a value the api sends is DATA and never an
 * identifier, so the three codes are map keys and a template built on them
 * (`client.paiement.moyen.${code}`) is refused twice over , the catalogue's
 * keys are three segments deep by its own spec, and a key assembled at runtime
 * is a key no instrument can see.
 */
const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

const MEAN_WORDS = new Map([
  ["orange", { name: "client.paiement.moyenOrange", detail: "client.paiement.moyenDetailOrange" }],
  ["wave", { name: "client.paiement.moyenWave", detail: "client.paiement.moyenDetailWave" }],
  ["moov", { name: "client.paiement.moyenMoov", detail: "client.paiement.moyenDetailMoov" }],
  ["sama", { name: "client.paiement.moyenSama", detail: "client.paiement.moyenDetailSama" }],
  ["virement", { name: "client.paiement.moyenVirement", detail: "client.paiement.moyenDetailVirement" }],
] as const);

/** Where the buyer stands in `WF-CLI-10` §7 to §9. */
export type PaymentStep = "choix" | "attente" | "confirme";

export interface PaiementProps {
  readonly fee: Fee;
  readonly intention: "financement" | "comptant";
  readonly vehicleLabel: string;
  readonly sellerName: string;
  /** `null` until `P1-DOS-010` seats one , rendered as a dash, never a guess. */
  readonly ownerName: string | null;
  readonly fileFees: FileFeesView | null;
  /** What financing the Hakili fee costs over the term, or `null` if unknown. */
  readonly extraInterestAmount: number | null;
  /** `C3-B.0`: the ONE estimate with the fee inside the loan, or `null` , a dash. */
  readonly estimateAmount: number | null;
  readonly mandate: ServedMandate | null;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onBack: () => void;
  /** Accepts the mandat, echoing the version displayed. */
  readonly onAcceptMandate: (input: {
    readonly feeFinancingRequested: boolean;
    readonly mandateVersion: number;
    readonly estimatedInstalmentAmount?: number;
  }) => void;
  readonly means: readonly MeanView[];
  readonly step: PaymentStep;
  /** The chosen means, and the buyer's own number on a push. */
  readonly mean: MeanView["code"];
  readonly msisdn: string;
  /** The number's country prefix, editable: not every buyer's mobile is Malian. */
  readonly prefix: string;
  readonly onMean: (code: MeanView["code"]) => void;
  readonly onMsisdn: (value: string) => void;
  readonly onPrefix: (value: string) => void;
  /**
   * §8's own figures: the account to send to, and **the reference to quote,
   * which is the DOSSIER's** , the architect's ruling of 11 September.
   *
   * `WF-CLI-10` §8 says « the dossier reference to quote » and the drawing
   * prints « Indiquez la référence H-2026-117 sur votre virement ». The payment
   * request's own `reference` is the `Idempotency-Key`: a key, and **a key is
   * not a thing a man writes on a transfer slip** , thirty-six characters
   * copied off a telephone at a bank counter is a mistyped reference and money
   * nobody can attribute. The key stays internal and is never shown.
   */
  readonly referenceToQuote: string;
  /** The chosen method's own destination, or `null` when none is configured for it. */
  readonly destination: PaymentDestination | null;
  readonly transferConfirmationHours: number;
  /** « Changer de moyen de paiement »: cancels the active declaration, then back to the choice. */
  readonly onChangeMean: () => void;
  /**
   * #493 addenda: the three visible states for every means , « coordonnées affichées »
   * (`declared` null), « paiement déclaré » , and the receipt. A declared payment shows
   * ONLY the declaration and the way to cancel it.
   */
  readonly declared: { readonly declaredAt: string; readonly reference: string | null } | null;
  readonly receiptReference: string;
  readonly onReceiptReference: (value: string) => void;
  readonly onDeclarePaid: (reference: string | null) => void;
  readonly onCancelDeclaration: () => void;
  /** §9: the buyer's own door out of `intake`. */
  readonly onLaunch: () => void;
  readonly onContinue: () => void;
}

/** Two letters at most, from the name the door gave. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/u)
    .filter((part) => part !== "")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

export function Paiement(props: PaiementProps): React.JSX.Element {
  /**
   * `WF-CLI-10` §9: **« N FCFA reçus », and the buyer decides the departure.**
   *
   * The launch is HIS gesture , the one row of `02` §2.4.1 a client's hand
   * moves , and it is offered here beside the receipt rather than on a screen
   * he has to find. The same control is on his tracking card, by the same door;
   * two places, one act, because he may close the app between them.
   */
  if (props.step === "confirme") {
    return (
      <div className="pb-20">
        <div className="px-5 pb-3 pt-5">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">
            {t("client.paiement.confirmeTitre")}
          </h1>
        </div>
        <div className="flex flex-col gap-3 px-5">
          <section
            data-testid="paiement-recu"
            className="rounded-2xl border border-emerald-200 bg-white px-5 py-6 text-center"
          >
            {/* `round C3-H`, the founder's finding on 65: the drawing's check
                in its green disc above the amount (`hakili-client.jsx`,
                `CheckCircle2`, `text-emerald-500` on `bg-emerald-50`). */}
            <div
              data-testid="paiement-recu-coche"
              className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50"
            >
              <CheckCircle2 className="h-6 w-6 text-emerald-500" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {amountOf(props.fee)} {t("client.paiement.recus")}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
              {t("client.paiement.conserveeJusquAu")}
            </p>
          </section>
          <section className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
            <p className="mb-1 text-sm font-medium text-gray-900">{t("client.paiement.vousDecidez")}</p>
            <p className="text-sm leading-relaxed text-gray-500">
              {t("client.paiement.nousMandatons")} {t("client.paiement.comptez48h")}
            </p>
            <button
              type="button"
              data-testid="paiement-lancer"
              disabled={props.busy}
              onClick={props.onLaunch}
              className="mt-3 w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
            >
              {t("client.paiement.lancerVerifications")}
            </button>
          </section>
          {props.refusal === null ? null : (
            <p role="alert" data-testid="paiement-refus" className="text-sm text-rose-600">
              {props.refusal}
            </p>
          )}
        </div>
      </div>
    );
  }

  /**
   * `WF-CLI-10` §8: **waiting, and what he reads while he waits.**
   *
   * **A mobile means is declared, not pushed.** While no provider is truly
   * connected (the founder's ruling of 16 October) every means is paid
   * off-platform and confirmed by Hakili staff, so the screen prints the
   * amount, the account and the reference like a transfer does, and says the
   * payment is declared and awaiting Hakili. It promises no demand on his
   * handset. This screen simply asks the door again.
   *
   * A transfer prints Hakili's account AND the dossier reference to quote ,
   * **on the screen**. The drawing says they are sent by WhatsApp; `messaging`
   * sends nothing here, and `P1-CLW-038`'s precedent is that a product does not
   * promise what it cannot keep. The divergence is in the addendum.
   */
  if (props.step === "attente") {
    const push = props.mean !== "virement";
    const meanName = t(MEAN_WORDS.get(props.mean)?.name ?? "client.paiement.moyenOrange");
    // « paiement déclaré par le client »: the declaration , and nothing else , is on the screen.
    if (props.declared !== null) {
      return (
        <div className="pb-20">
          <div className="px-5 pb-3 pt-5">
            <h1 className="text-xl font-semibold tracking-tight text-gray-900">
              {t("client.paiement.declareTitre")}
            </h1>
          </div>
          <div className="flex flex-col gap-3 px-5">
            <section
              data-testid="paiement-attente"
              className="rounded-2xl border border-indigo-200 bg-white px-5 py-6 text-center"
            >
              <p className="text-sm font-medium text-gray-700">{meanName}</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{amountOf(props.fee)}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
                {t("client.paiement.paiementDeclare")}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-gray-400">
                {`${t("client.paiement.confirmationSous")} ${String(props.transferConfirmationHours)} ${t("client.paiement.heuresOuvreesDepuisDeclaration")}`}
              </p>
              <dl className="mt-3 flex flex-col gap-1.5 text-xs text-gray-500">
                <div className="flex justify-between gap-3">
                  <dt>{t("client.paiement.referenceAciter")}</dt>
                  <dd data-testid="paiement-reference" className="font-medium tabular-nums text-gray-900">
                    {props.referenceToQuote}
                  </dd>
                </div>
                {props.declared.reference === null ? null : (
                  <div className="flex justify-between gap-3">
                    <dt>{t("client.paiement.referenceRecu")}</dt>
                    <dd className="font-medium tabular-nums text-gray-900">{props.declared.reference}</dd>
                  </div>
                )}
              </dl>
            </section>
            <button
              type="button"
              data-testid="paiement-annuler"
              disabled={props.busy}
              onClick={props.onCancelDeclaration}
              className="text-xs font-medium text-indigo-600 disabled:opacity-40"
            >
              {t("client.paiement.annulerDeclaration")}
            </button>
            {props.refusal === null ? null : (
              <p role="alert" data-testid="paiement-refus" className="text-sm text-rose-600">
                {props.refusal}
              </p>
            )}
          </div>
        </div>
      );
    }
    // « coordonnées affichées »: the details, the reference to quote, and the client's own next act.
    return (
      <div className="pb-20">
        <div className="px-5 pb-3 pt-5">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">
            {push ? t("client.paiement.payezTitre") : t("client.paiement.virementTitre")}
          </h1>
        </div>
        <div className="flex flex-col gap-3 px-5">
          <section
            data-testid="paiement-attente"
            className="rounded-2xl border border-indigo-200 bg-white px-5 py-6 text-center"
          >
            <p className="text-sm font-semibold text-gray-900">
              {push ? amountOf(props.fee) : t("client.paiement.virementAttendu")}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500">
              {push
                ? t("client.paiement.payezSurCompte")
                : `${t("client.paiement.confirmationSous")} ${String(props.transferConfirmationHours)} ${t("client.paiement.heuresOuvrees")}`}
            </p>
          </section>

          {props.destination === null ? (
            <section
              data-testid="paiement-coordonnees-absentes"
              className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
            >
              <p className="mb-2 text-sm text-gray-700">{t("client.paiement.coordonneesAbsentes")}</p>
              <dl className="text-xs text-gray-500">
                <div className="flex justify-between gap-3">
                  <dt>{t("client.paiement.referenceAciter")}</dt>
                  <dd data-testid="paiement-reference" className="font-medium tabular-nums text-gray-900">
                    {props.referenceToQuote}
                  </dd>
                </div>
              </dl>
            </section>
          ) : (
            <section
              data-testid="paiement-coordonnees"
              className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
            >
              <p className="mb-2 text-sm font-medium text-gray-900">
                {props.destination.kind === "bank"
                  ? t("client.paiement.coordonneesTitre")
                  : t("client.paiement.coordonneesMobileTitre").replace("{moyen}", meanName)}
              </p>
              <dl className="flex flex-col gap-1.5 text-xs text-gray-500">
                {props.destination.kind === "bank" ? (
                  <>
                    <div className="flex justify-between gap-3">
                      <dt>{t("client.paiement.banque")}</dt>
                      <dd className="font-medium text-gray-900">{props.destination.bankName}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt>{t("client.paiement.titulaire")}</dt>
                      <dd className="font-medium text-gray-900">{props.destination.accountName}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt>{t("client.paiement.numero")}</dt>
                      <dd className="font-medium tabular-nums text-gray-900">
                        {props.destination.accountNumber}
                      </dd>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between gap-3">
                    <dt>{t("client.paiement.numero")}</dt>
                    <dd
                      data-testid="paiement-numero-hakili"
                      className="font-medium tabular-nums text-gray-900"
                    >
                      {props.destination.number}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <dt>{t("client.paiement.montantAPayer")}</dt>
                  <dd className="font-medium tabular-nums text-gray-900">{amountOf(props.fee)}</dd>
                </div>
                {/* **The reference is what matches his payment to his file.**
                    A statement line with no reference is money nobody can
                    attribute, and the staff door that confirms it asks for
                    exactly this. */}
                <div className="flex justify-between gap-3">
                  <dt>{t("client.paiement.referenceAciter")}</dt>
                  <dd data-testid="paiement-reference" className="font-medium tabular-nums text-gray-900">
                    {props.referenceToQuote}
                  </dd>
                </div>
              </dl>
            </section>
          )}

          <label className="block text-xs text-gray-500" htmlFor="paiement-recu-ref">
            {t("client.paiement.referenceRecuFacultative")}
            <input
              id="paiement-recu-ref"
              data-testid="paiement-recu-ref"
              value={props.receiptReference}
              onChange={(event) => {
                props.onReceiptReference(event.target.value);
              }}
              className="mt-1 block w-full rounded-2xl border border-gray-200 px-4 py-3 text-base font-medium outline-none"
            />
          </label>
          <button
            type="button"
            data-testid="paiement-jai-paye"
            disabled={props.busy}
            onClick={() => {
              props.onDeclarePaid(
                props.receiptReference.trim() === "" ? null : props.receiptReference.trim(),
              );
            }}
            className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {t("client.paiement.jaiPaye")}
          </button>
          <button
            type="button"
            data-testid="paiement-changer-moyen"
            disabled={props.busy}
            onClick={props.onChangeMean}
            className="text-xs font-medium text-indigo-600 disabled:opacity-40"
          >
            {t("client.paiement.changerMoyen")}
          </button>
          {props.refusal === null ? null : (
            <p role="alert" data-testid="paiement-refus" className="text-sm text-rose-600">
              {props.refusal}
            </p>
          )}
        </div>
      </div>
    );
  }

  const isFinancing = props.intention === "financement";
  const [readMandate, setReadMandate] = useState(false);
  /**
   * `WF-CLI-10` §4: **two radios and no preselection.**
   *
   * `null` until he chooses, so « Les régler à la signature » is never the
   * product's answer on his behalf. The door refuses an acceptance without a
   * choice for the same reason, in the same words.
   */
  const [financed, setFinanced] = useState<boolean | null>(
    props.mandate?.accepted?.feeFinancingRequested ?? null,
  );
  /**
   * **`!= null`, and the two faults it cures are both mine.**
   *
   * The rename pass turned `accepted` into `acceptedd` inside a property path ,
   * a second casualty of replacing identifiers by substring , so this read an
   * absent field and was always `undefined !== null`, which is TRUE. The guard
   * unlocked before anybody had accepted anything.
   *
   * And even spelled right, `!== null` is wrong while the mandate is still
   * loading: `props.mandate` is null then, `?.accepted` is `undefined`, and
   * `undefined !== null` is true again. **A buyer would have met a live
   * « Recevoir la demande de paiement » for the half-second before the clauses
   * arrived** , which is exactly the guard `WF-CLI-10` puts there.
   *
   * Written as two explicit checks and not `!= null`: `eqeqeq` refuses the
   * loose form, and it is right that a rule about equality does not carve out
   * the one operator whose whole point is to be loose.
   */
  const [accepted, setAccepted] = useState(props.mandate !== null && props.mandate.accepted !== null);

  const readyToContinue = !isFinancing || accepted;

  return (
    <div className="pb-20">
      <div className="flex items-center gap-3 px-5 pb-3 pt-5">
        <button
          type="button"
          data-testid="paiement-retour"
          aria-label={t("client.paiement.retour")}
          onClick={props.onBack}
          className="text-gray-500"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-lg font-semibold tracking-tight text-gray-900">{t("client.paiement.titre")}</h1>
      </div>

      <div className="flex flex-col gap-3 px-5">
        {/* §1: the amount in large type, the car, and the two promises. */}
        <section
          data-testid="paiement-vehicule"
          className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
        >
          <p data-testid="paiement-montant" className="text-2xl font-semibold tabular-nums text-gray-900">
            {amountOf(props.fee)}
          </p>
          {props.fee.unpublished === undefined ? null : (
            <p data-testid="paiement-tarif-non-publie" className="mt-1 text-xs text-amber-700">
              {props.fee.unpublished}
            </p>
          )}
          <p data-testid="paiement-promesses" className="mt-1 text-xs leading-relaxed text-gray-500">
            {t("client.paiement.objet")} {props.vehicleLabel} {t("client.paiement.de")} {props.sellerName}.{" "}
            {t("client.paiement.apresReception")}
          </p>
        </section>

        {/* §2: who is following the file , a dash while nobody is. */}
        <section
          data-testid="paiement-owner"
          className="rounded-2xl border border-gray-200 bg-white px-4 py-3"
        >
          <p className="flex items-center gap-2.5 text-xs leading-relaxed text-gray-500">
            {props.ownerName === null ? (
              <span data-testid="paiement-owner-absent">{t("client.paiement.chargeInconnu")}</span>
            ) : (
              <>
                {/* R23 (c): the drawing's initials circle beside the chargé's name. */}
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700"
                  data-testid="paiement-owner-initiales"
                  aria-hidden="true"
                >
                  {initialsOf(props.ownerName)}
                </span>
                <span>
                  <span className="font-medium text-gray-900">{props.ownerName}</span>{" "}
                  {t("client.paiement.suitVotreDossier")}
                </span>
              </>
            )}
          </p>
        </section>

        {/* §3: the guarantee that makes paying before the check safe. */}
        <section
          data-testid="paiement-remboursement"
          className="flex items-start gap-2.5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3.5"
        >
          <Shield className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <p className="text-xs leading-relaxed text-gray-900">
            <span className="font-medium">{t("client.paiement.remboursementFort")}</span>{" "}
            {t("client.paiement.remboursementSuite")}
          </p>
        </section>

        {isFinancing && props.mandate !== null && props.fileFees !== null ? (
          <section
            data-testid="paiement-mandat"
            className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
          >
            <p className="text-sm font-semibold text-gray-900">{t("client.paiement.mandatTitre")}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
              {t("client.paiement.mandatResume")}{" "}
              <span className="font-medium">{formatMoney(props.fileFees.hakiliAmount)}</span>
              {t("client.paiement.mandatDeuxPourCent")}{" "}
              {/* `C3-B.1`: the mandate's line , the fourth reading of the one
                  estimate, in the paragraph's own type. */}
              <span data-testid="paiement-mandat-mensualite">
                {/* R-G: the mandate's line prints the figure the mandate door SERVES , the one the buyer
                    echoes at acceptance , so what he engages on and what he read are one number by
                    construction; the quote door's figure stands only where the mandate serves none. */}
                {fill("client.paiement.mandatMensualite", {
                  amount:
                    (props.mandate.estimate?.instalmentAmount ?? props.estimateAmount) === null
                      ? t("common.valeur.tiret")
                      : formatMoney(props.mandate.estimate?.instalmentAmount ?? props.estimateAmount ?? 0),
                })}
              </span>
            </p>

            {/* The five clauses, SERVED , this file holds none of the words. */}
            {readMandate ? (
              <div data-testid="paiement-mandat-clauses" className="mt-3 border-t border-gray-100 pt-3">
                {props.mandate.clauses.map((clause) => (
                  <div key={clause.title} className="mb-2">
                    <p className="text-xs font-medium text-gray-900">{clause.title}</p>
                    <p className="text-xs leading-relaxed text-gray-500">{clause.body}</p>
                  </div>
                ))}
              </div>
            ) : null}
            <button
              type="button"
              data-testid="paiement-mandat-lire"
              onClick={() => {
                setReadMandate(!readMandate);
              }}
              className="mt-2 text-xs font-medium text-indigo-600"
            >
              {readMandate ? t("client.paiement.masquerMandat") : t("client.paiement.lireMandat")}
            </button>

            {/* The two settlement options, with the door's own figures. */}
            <div className="mt-3 border-t border-gray-100 pt-3">
              <p className="mb-2 text-xs font-medium text-gray-900">{t("client.paiement.commentRegler")}</p>
              {[true, false].map((option) => (
                <button
                  key={String(option)}
                  type="button"
                  data-testid={option ? "paiement-frais-inclus" : "paiement-frais-signature"}
                  onClick={() => {
                    setFinanced(option);
                  }}
                  className={`mb-2 flex w-full items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left ${
                    financed === option ? "border-indigo-200 bg-indigo-50" : "border-gray-200 bg-white"
                  }`}
                >
                  <span
                    className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
                      financed === option ? "border-indigo-600 bg-indigo-600" : "border-gray-200"
                    }`}
                  />
                  <span>
                    <span className="block text-xs font-medium text-gray-900">
                      {option ? t("client.paiement.inclusTitre") : t("client.paiement.signatureTitre")}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
                      {option ? t("client.paiement.inclusDetail") : t("client.paiement.signatureDetail")}
                    </span>
                    <span
                      className={`mt-1 block text-xs font-medium ${
                        option ? "text-amber-700" : "text-emerald-700"
                      }`}
                    >
                      {option
                        ? /**
                           * The surcharge, or a DASH , `R81`. `null` means the
                           * platform could not compute it (no settings in force
                           * for the day), and « + 0 FCFA d'intérêts » beside
                           * « Les inclure dans le isFinancing » would read as
                           * « this costs you nothing ».
                           */
                          props.extraInterestAmount === null
                          ? t("common.valeur.tiret")
                          : `${t("client.paiement.plus")} ${formatMoney(props.extraInterestAmount)} ${t("client.paiement.interetsSurLaDuree")}`
                        : t("client.paiement.aucunInteret")}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            <button
              type="button"
              data-testid="paiement-mandat-accepted"
              disabled={financed === null || props.busy}
              onClick={() => {
                if (financed === null || props.mandate === null) return;
                setAccepted(true);
                props.onAcceptMandate({
                  feeFinancingRequested: financed,
                  // **The version DISPLAYED**, echoed back. A stale bundle is
                  // refused by the door rather than recorded as consent to a
                  // text this buyer never read.
                  mandateVersion: props.mandate.version,
                  // R-G: the estimate DISPLAYED, echoed the same way.
                  ...(props.mandate.estimate === null
                    ? {}
                    : { estimatedInstalmentAmount: props.mandate.estimate.instalmentAmount }),
                });
              }}
              className="mt-3 flex w-full items-start gap-2.5 text-left disabled:opacity-40"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                  accepted ? "border-indigo-600 bg-indigo-600" : "border-gray-200"
                }`}
              />
              <span className="text-xs leading-relaxed text-gray-900">
                {t("client.paiement.jAccepte")} {formatMoney(props.fileFees.hakiliAmount)}{" "}
                {t("client.paiement.dusUniquement")}
              </span>
            </button>
          </section>
        ) : null}

        {/* §5: the cash path , no mandat, no frais de dossier. */}
        {isFinancing ? null : (
          <section
            data-testid="paiement-comptant"
            className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
          >
            <p className="text-sm font-semibold text-gray-900">{t("client.paiement.comptantTitre")}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-gray-500">
              {t("client.paiement.comptantDetail")}
            </p>
          </section>
        )}

        {/* §6: the five means in the record's order (`round PAY-M`), and which
            are OFFERED is the settings' word. The two new rows follow the
            drawing's row shape; the designer's stroke is owed. */}
        <p className="mt-2 text-sm font-medium text-gray-900">{t("client.paiement.commentPayer")}</p>
        <div className="flex flex-col gap-2">
          {inRecordOrder(props.means).map((mean) => (
            <button
              key={mean.code}
              type="button"
              data-testid={`paiement-moyen-${mean.code}`}
              disabled={!mean.available}
              onClick={() => {
                props.onMean(mean.code);
              }}
              className={`flex w-full items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-left disabled:opacity-40 ${
                props.mean === mean.code ? "border-indigo-200" : "border-gray-200"
              }`}
            >
              <span
                className={`h-5 w-5 shrink-0 rounded-full border-2 ${
                  props.mean === mean.code ? "border-indigo-600 bg-indigo-600" : "border-gray-200"
                }`}
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-gray-900">
                  {t(MEAN_WORDS.get(mean.code)?.name ?? "client.paiement.moyenOrange")}
                </span>
                {/* **A means that is not offered says WHY**, on the control
                    itself. `R81`: absent tells him nothing, present and dead
                    tells him something false. */}
                <span className="block text-xs text-gray-500">
                  {mean.available
                    ? t(MEAN_WORDS.get(mean.code)?.detail ?? "client.paiement.moyenDetailOrange")
                    : (mean.reason ?? t("client.paiement.moyenIndisponible"))}
                </span>
              </span>
            </button>
          ))}
        </div>

        {props.mean === "virement" ? null : (
          <section
            data-testid="paiement-numero"
            className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
          >
            {/* `PAY-M.5`, to the drawing: the label names the MEANS (« Numéro
                Orange Money »), the indicatif stands in its own box, and the
                number is the buyer's own from his session, editable , the
                door's rule of `C3-A` unchanged, the msisdn taken again on a
                re-send. */}
            <label className="mb-2 block text-sm text-gray-500" htmlFor="paiement-msisdn">
              {t("client.paiement.numeroMoyen").replace(
                "{moyen}",
                t(MEAN_WORDS.get(props.mean)?.name ?? "client.paiement.moyenOrange"),
              )}
            </label>
            <div className="flex items-center gap-2">
              <div
                data-testid="paiement-indicatif"
                className="flex items-center rounded-2xl border border-gray-200 bg-white px-3 py-3"
              >
                <input
                  data-testid="paiement-indicatif-saisie"
                  aria-label={t("client.paiement.indicatifLabel")}
                  inputMode="tel"
                  size={5}
                  value={props.prefix}
                  onChange={(event) => {
                    props.onPrefix(event.target.value);
                  }}
                  className="w-14 text-sm font-medium tabular-nums outline-none"
                />
              </div>
              <input
                id="paiement-msisdn"
                data-testid="paiement-msisdn"
                inputMode="tel"
                value={props.msisdn}
                onChange={(event) => {
                  props.onMsisdn(event.target.value);
                }}
                className="flex-1 rounded-2xl border border-gray-200 px-4 py-3 text-base font-medium tabular-nums outline-none"
              />
            </div>
            <p className="mt-2 text-xs leading-relaxed text-gray-400">{t("client.paiement.numeroAide")}</p>
          </section>
        )}

        {props.refusal === null ? null : (
          <p role="alert" data-testid="paiement-refus" className="text-sm text-rose-600">
            {props.refusal}
          </p>
        )}

        <button
          type="button"
          data-testid="paiement-continuer"
          disabled={!readyToContinue || props.busy || props.fee.unpublished !== undefined}
          {...(props.fee.unpublished === undefined ? {} : { title: props.fee.unpublished })}
          onClick={props.onContinue}
          className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {/* §7: the control's word is the MEANS', not the screen's mood. */}
          {props.mean === "virement"
            ? t("client.paiement.recevoirCoordonnees")
            : t("client.paiement.afficherCoordonnees")}
        </button>
        {readyToContinue ? null : (
          <p data-testid="paiement-mandat-requis" className="text-center text-xs text-gray-400">
            {t("client.paiement.accepterPourContinuer")}
          </p>
        )}
      </div>
    </div>
  );
}
